import { create } from 'zustand';
import { apiService } from '@/services/api';
import type {
  Transaction,
  TransactionAccount,
  TransactionPageResponse,
  TransactionListParameters,
  TransactionSearchFilter,
  SibsTransaction,
  StoredConsent,
} from '@/types';

// Helper to convert SIBS transaction to Transaction
function sibsTransactionToTransaction(
  sibsTx: SibsTransaction,
  _bankAccountId: string,
  index: number
): Transaction {
  const amount = parseFloat(sibsTx.transactionAmount.amount);
  const currency = sibsTx.transactionAmount.currency;

  // Determine description from various fields
  const description =
    sibsTx.remittanceInformationUnstructured ||
    sibsTx.remittanceInformationStructured ||
    sibsTx.creditorName ||
    sibsTx.debtorName ||
    'Transaction';

  // Get running balance if available
  const runningBalance = sibsTx.balanceAfterTransaction
    ? parseFloat(sibsTx.balanceAfterTransaction.balanceAmount.amount)
    : 0;

  return {
    id: index + 1, // SIBS-fetched transactions use index as temporary ID
    bankAccountId: 0, // SIBS-fetched transactions don't have a database account ID
    transactionDate: sibsTx.valueDate || sibsTx.bookingDate || new Date().toISOString(),
    bookingDate: sibsTx.bookingDate || new Date().toISOString(),
    amount,
    currency,
    runningBalance,
    description,
    type: amount < 0 ? 'debit' : 'credit',
    status: 'booked',
    category: sibsTx.bankTransactionCode || undefined,
    // SIBS enrichment fields
    creditorIban: sibsTx.creditorAccount?.iban,
    debtorIban: sibsTx.debtorAccount?.iban,
    bankTransactionCode: sibsTx.bankTransactionCode,
    remittanceStructured: sibsTx.remittanceInformationStructured,
  };
}

/** Movements per page of the infinite scroll. */
export const TRANSACTION_PAGE_SIZE = 50;

/** The query the loaded pages belong to: every later page must ask the same. */
interface TransactionQuery {
  accountId: number | null;
  filters: TransactionSearchFilter;
}

// The query of the list loaded last, and a counter bumped by every new one (or a reset): a page
// answering an older query (the filters changed while it was in flight) is dropped, so a slow
// response never mixes into the new list.
let activeQuery: TransactionQuery | null = null;
let queryGeneration = 0;

const pageParams = (query: TransactionQuery, cursor: string | null): Record<string, unknown> => {
  const f = query.filters;
  return {
    accountId: query.accountId ?? undefined,
    cursor: cursor ?? undefined,
    pageSize: TRANSACTION_PAGE_SIZE,
    searchString: f.searchString || undefined,
    startDate: f.startDate || undefined,
    endDate: f.endDate || undefined,
    minAmount: f.minAmount,
    maxAmount: f.maxAmount,
    minBalance: f.minBalance,
    maxBalance: f.maxBalance,
    type: f.type || undefined,
    status: f.status || undefined,
  };
};

// Adds the accounts not seen yet. Returns `into` itself when every one was already there, so rows
// memoised on it don't all re-render with each page.
const accountsById = (accounts: TransactionAccount[] | undefined, into: Record<number, TransactionAccount> = {}) => {
  const added = (accounts ?? []).filter((account) => !(account.id in into));
  if (added.length === 0) return into;
  const merged = { ...into };
  for (const account of added) merged[account.id] = account;
  return merged;
};

interface TransactionStore {
  // State
  transactions: Transaction[];
  isLoading: boolean;
  /** A later page is loading (the rows already shown stay). */
  isLoadingMore: boolean;
  /** Why the last later page failed; the list stays, and loadMoreTransactions retries. */
  loadMoreError: string | null;
  error: string | null;
  listParameters: TransactionListParameters;
  totalCount: number;
  /** Cursor of the next page; null once the end is reached. */
  nextCursor: string | null;
  hasMore: boolean;
  /** Across accounts: the account (label, logo) of every loaded row. */
  transactionAccounts: Record<number, TransactionAccount>;
  currentAccountId: number | null;
  sibsTransactionsLoaded: boolean;

  // Actions
  /** First page of one account's movements, with the current search filters. */
  fetchTransactions: (accountId: number) => Promise<void>;
  /** First page across every account (or `filters.accountId`), with these filters. */
  fetchAllTransactions: (filters?: TransactionSearchFilter) => Promise<void>;
  /** Appends the next page of the list loaded last; a no-op at the end or while one loads. */
  loadMoreTransactions: () => Promise<void>;
  fetchSibsTransactions: (
    consent: StoredConsent,
    accountId: string,
    dateFrom?: string,
    dateTo?: string
  ) => Promise<void>;
  setSearchFilters: (filters: TransactionSearchFilter) => void;
  setCurrentPage: (page: number) => void;
  setSortBy: (sortBy: string, ascending: boolean) => void;
  clearError: () => void;
  reset: () => void;
}

// Loads the first page of a new query, replacing the list.
const loadFirstPage = async (set: (partial: Partial<TransactionStore>) => void, next: TransactionQuery) => {
  const generation = ++queryGeneration;
  activeQuery = next;
  set({
    isLoading: true,
    isLoadingMore: false,
    loadMoreError: null,
    error: null,
    currentAccountId: next.accountId,
    nextCursor: null,
    hasMore: false,
  });
  try {
    const response = await apiService.get<TransactionPageResponse>('/transaction/page', pageParams(next, null));
    if (generation !== queryGeneration) return;
    const page = response.value;
    set({
      transactions: page?.transactions ?? [],
      totalCount: page?.totalCount ?? page?.transactions.length ?? 0,
      nextCursor: page?.nextCursor ?? null,
      hasMore: !!page?.nextCursor,
      transactionAccounts: accountsById(page?.accounts),
      isLoading: false,
    });
  } catch (error) {
    if (generation !== queryGeneration) return;
    set({
      error: error instanceof Error ? error.message : 'Failed to fetch transactions',
      isLoading: false,
    });
  }
};

export const useTransactionStore = create<TransactionStore>((set, get) => ({
  // INITIAL STATE
  transactions: [],
  isLoading: false,
  isLoadingMore: false,
  loadMoreError: null,
  error: null,
  totalCount: 0,
  nextCursor: null,
  hasMore: false,
  transactionAccounts: {},
  currentAccountId: null,
  sibsTransactionsLoaded: false,
  listParameters: {
    currentPage: 1,
    pageSize: TRANSACTION_PAGE_SIZE,
    sortByParameter: 'transactionDate',
    sortAscending: false,
  },

  // ASYNC ACTIONS
  fetchTransactions: (accountId: number) =>
    loadFirstPage(set, { accountId, filters: get().listParameters.searchFilters ?? {} }),

  fetchAllTransactions: (filters?: TransactionSearchFilter) => {
    const searchFilters = filters ?? get().listParameters.searchFilters ?? {};
    return loadFirstPage(set, { accountId: searchFilters.accountId ?? null, filters: searchFilters });
  },

  loadMoreTransactions: async () => {
    const { nextCursor, isLoading, isLoadingMore } = get();
    const query = activeQuery;
    if (!query || !nextCursor || isLoading || isLoadingMore) return;

    const generation = queryGeneration;
    set({ isLoadingMore: true, loadMoreError: null });
    try {
      const response = await apiService.get<TransactionPageResponse>('/transaction/page', pageParams(query, nextCursor));
      if (generation !== queryGeneration) return;
      const page = response.value;
      set((state) => {
        // Keyset pages never overlap; the guard only keeps a row from ever showing twice.
        const loaded = new Set(state.transactions.map((t) => t.id));
        return {
          transactions: [...state.transactions, ...(page?.transactions ?? []).filter((t) => !loaded.has(t.id))],
          nextCursor: page?.nextCursor ?? null,
          hasMore: !!page?.nextCursor,
          transactionAccounts: accountsById(page?.accounts, state.transactionAccounts),
          isLoadingMore: false,
        };
      });
    } catch (error) {
      if (generation !== queryGeneration) return;
      set({
        loadMoreError: error instanceof Error ? error.message : 'Failed to load more transactions',
        isLoadingMore: false,
      });
    }
  },

  // Fetch transactions from SIBS API using consent
  fetchSibsTransactions: async (
    consent: StoredConsent,
    accountId: string,
    dateFrom?: string,
    dateTo?: string
  ) => {
    // A live bank read replaces the stored list: no later stored page belongs after it.
    activeQuery = null;
    queryGeneration++;
    set({ isLoading: true, error: null, currentAccountId: 0, nextCursor: null, hasMore: false });
    try {
      // Use filters if provided, otherwise default to last 90 days
      const params = get().listParameters;
      const effectiveDateFrom =
        dateFrom ||
        params.searchFilters?.startDate ||
        new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      const effectiveDateTo =
        dateTo ||
        params.searchFilters?.endDate ||
        new Date().toISOString().split('T')[0];

      const response = await apiService.getSibsTransactions(
        consent.aspspCde,
        consent.consentId,
        accountId,
        effectiveDateFrom,
        effectiveDateTo
      );

      if (response.value?.transactions) {
        const bookedTransactions = response.value.transactions.booked || [];
        const pendingTransactions = response.value.transactions.pending || [];

        // Convert SIBS transactions to our Transaction format
        const allTransactions: Transaction[] = [
          ...bookedTransactions.map((tx, idx) =>
            sibsTransactionToTransaction(tx, accountId, idx)
          ),
          ...pendingTransactions.map((tx, idx) => ({
            ...sibsTransactionToTransaction(tx, accountId, bookedTransactions.length + idx),
            status: 'pending' as const,
          })),
        ];

        // Sort by transaction date (newest first by default)
        allTransactions.sort((a, b) => {
          const dateA = new Date(a.transactionDate).getTime();
          const dateB = new Date(b.transactionDate).getTime();
          return params.sortAscending ? dateA - dateB : dateB - dateA;
        });

        // Apply client-side filtering for amount range
        let filteredTransactions = allTransactions;
        if (params.searchFilters) {
          if (params.searchFilters.minAmount !== undefined) {
            filteredTransactions = filteredTransactions.filter(
              (tx) => Math.abs(tx.amount) >= params.searchFilters!.minAmount!
            );
          }
          if (params.searchFilters.maxAmount !== undefined) {
            filteredTransactions = filteredTransactions.filter(
              (tx) => Math.abs(tx.amount) <= params.searchFilters!.maxAmount!
            );
          }
          if (params.searchFilters.searchString) {
            const searchLower = params.searchFilters.searchString.toLowerCase();
            filteredTransactions = filteredTransactions.filter((tx) =>
              tx.description.toLowerCase().includes(searchLower)
            );
          }
        }

        set({
          transactions: filteredTransactions,
          totalCount: filteredTransactions.length,
          sibsTransactionsLoaded: true,
          isLoading: false,
        });
      }
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch SIBS transactions',
        isLoading: false,
      });
    }
  },

  // SYNCHRONOUS ACTIONS
  setSearchFilters: (filters: TransactionSearchFilter) => {
    set((state) => ({
      listParameters: {
        ...state.listParameters,
        searchFilters: filters,
        currentPage: 1,
      },
    }));
  },

  setCurrentPage: (page: number) => {
    set((state) => ({
      listParameters: {
        ...state.listParameters,
        currentPage: page,
      },
    }));
  },

  setSortBy: (sortBy: string, ascending: boolean) => {
    set((state) => ({
      listParameters: {
        ...state.listParameters,
        sortByParameter: sortBy,
        sortAscending: ascending,
        currentPage: 1,
      },
    }));
  },

  clearError: () => set({ error: null }),

  reset: () => {
    // Pages still in flight belong to the list being dropped.
    activeQuery = null;
    queryGeneration++;
    set({
      transactions: [],
      isLoading: false,
      isLoadingMore: false,
      loadMoreError: null,
      error: null,
      totalCount: 0,
      nextCursor: null,
      hasMore: false,
      transactionAccounts: {},
      currentAccountId: null,
      sibsTransactionsLoaded: false,
      listParameters: {
        currentPage: 1,
        pageSize: TRANSACTION_PAGE_SIZE,
        sortByParameter: 'transactionDate',
        sortAscending: false,
      },
    });
  },
}));
